import { useQuery } from '@tanstack/react-query';
import { useDataChainId } from '@ui/hooks/datachain';
import { getLVMONRedeemer } from '@ui/services/contract/contract-instances/getLVMONRedeemer';
import { Token } from '@ui/types/token';
import { Address, formatUnits, parseUnits, zeroAddress } from 'viem';
import { usePublicClient } from 'wagmi';

export const useStandardRedeemQuote = ({
  inputToken,
  outputToken,
  inputAmount,
}: {
  inputToken?: Token;
  outputToken?: Token;
  inputAmount: string;
}) => {
  const chainId = useDataChainId();
  const publicClient = usePublicClient({ chainId });

  const isEnabled =
    !!inputToken?.address &&
    !!outputToken?.address &&
    !!inputAmount &&
    !!inputToken.decimals &&
    !!outputToken.decimals &&
    parseFloat(inputAmount) > 0;

  return useQuery({
    queryKey: [
      'standard-redeem-quote',
      chainId,
      inputToken?.address,
      outputToken?.address,
      inputAmount,
      !!publicClient,
    ],
    queryFn: async () => {
      if (!publicClient || !inputToken || !outputToken || !inputAmount) {
        return null;
      }

      const lvmonRedeemer = getLVMONRedeemer({
        chainId,
        publicClient: publicClient as any,
      });

      const amountIn = parseUnits(inputAmount, inputToken.decimals);
      if (amountIn === 0n) return null;

      try {
        const result = await lvmonRedeemer.read.previewStandardRedeem([
            outputToken.address as Address, // reserveToken
            amountIn, // amount
        ]);

        return {
          amountOut: result,
          formattedAmountOut: formatUnits(result, outputToken.decimals),
          bestCandidate: {
            adapter: lvmonRedeemer.address,
            data: '0x' as `0x${string}`,
          },
        };
      } catch (error) {
        console.error('[useStandardRedeemQuote] Error fetching quote:', error);
        return null;
      }
    },
    enabled: isEnabled,
    refetchInterval: 10000,
  });
};
